import QRCode from 'qrcode';
import { mapState } from 'vuex';
import {
  GENERATE_QR_CODE,
  CHECK_QR_CODE_STATUS,
  HANDLE_QR_CODE_LOGIN
} from '@store/officialTopupNew/login';
import {
  UPDATE_LOADING,
  SWITCH_SERVER,
  UPDATE_PC_SERVER_ID,
  UPDATE_ROLE_LIST,
  GET_SERVER_LIST
} from '@store/officialTopupNew/';
import globalConfig from '@utiles/officialStore/globalConfig';
// 二维码状态枚举
export const QRCodeStatus = {
  INIT: 'init', // 初始化状态
  WAITING: 'waiting', // 等待扫码
  SCANNED: 'scanned', // 已扫码，待授权
  EXPIRED: 'expired', // 二维码已过期
  CONFIRMED: 'confirmed', // 已授权确认
  LOGGING: 'logging', // 登录中
  UNAUTHORIZED: 'unauthorized' // 拒绝授权
};

/**
 * 扫码登录 Mixin
 */
export default {
  data() {
    return {
      // 二维码图片URL（base64格式）
      qrCodeUrl: '',
      // 二维码token（用于轮询查询状态）
      qrCode: '',
      // 扫码状态（完全由服务端接口返回决定）
      qrStatus: QRCodeStatus.INIT,
      // 加载状态（刷新二维码时显示）
      qrCodeLoading: false,
      // 轮询定时器（定期检查二维码状态）
      qrCodePollTimer: null,
      // 导出状态枚举供模板使用
      QRCodeStatus,
      pkgName: globalConfig.getHrefParam('packageName') || ''
    };
  },

  computed: {
    ...mapState({
      letter: state => state.letter,
      gameId: state => state.gameId,
      picList: state => state.page.picList
    })
  },

  methods: {
    /**
     * 生成二维码图片
     * @param {string} code - 二维码内容
     * @returns {Promise<string>} 二维码图片的 base64 URL
     */
    async generateQRCodeImage(code) {
      try {
        const url = await QRCode.toDataURL(code, {
          width: 200,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        return url;
      } catch (error) {
        console.log('生成二维码失败:', error);
        return '';
      }
    },

    /**
     * 获取二维码
     * 从服务端获取二维码token并生成二维码图片
     * 获取成功后立即开始轮询检查状态
     */
    async getQRCode() {
      try {
        this.qrCodeLoading = true;
        const res = await this.$store.dispatch(GENERATE_QR_CODE, {
          game_id: this.gameId,
          package_name: this.pkgName
        });

        if (res?.result == 1 && res?.data?.token) {
          this.qrCode = res.data.token;
          // 生成二维码图片（base64格式）
          const imageUrl = await this.generateQRCodeImage(res.data.token);
          this.qrCodeUrl = imageUrl;
          this.qrStatus = QRCodeStatus.WAITING;

          // 开始轮询检查扫码状态
          this.startQRCodePolling();
        } else {
          console.log('获取二维码失败:', res);
          this.qrStatus = QRCodeStatus.INIT;
        }
      } catch (error) {
        console.log('获取二维码异常:', error);
        this.qrStatus = QRCodeStatus.INIT;
      } finally {
        this.qrCodeLoading = false;
      }
    },

    /**
     * 检查二维码状态
     * 完全依靠接口返回的状态来判断二维码的各种状态
     */
    async checkQRCodeStatus() {
      if (!this.qrCode) return;

      try {
        const res = await this.$store.dispatch(CHECK_QR_CODE_STATUS, {
          token: this.qrCode,
          game_id: this.gameId,
          package_name: this.pkgName
        });

        if (res?.result == 1 && res?.data?.qrCodeStatus) {
          const status = res.data?.qrCodeStatus;

          switch (+status) {
            case 1: // 待扫码
              this.qrStatus = QRCodeStatus.WAITING;
              break;

            case 2: // 已扫码，待授权
              this.qrStatus = QRCodeStatus.SCANNED;
              break;

            case 3: // 已授权确认，需要登录
              this.qrStatus = QRCodeStatus.CONFIRMED;
              // 停止轮询
              this.stopQRCodePolling();
              // 执行登录逻辑
              await this.handleQRCodeLogin();
              break;

            case 4: // 用户拒绝授权
              this.qrStatus = QRCodeStatus.UNAUTHORIZED;
              // 停止轮询，等待用户手动刷新
              this.stopQRCodePolling();
              break;

            case 5: // 二维码已过期
              this.qrStatus = QRCodeStatus.EXPIRED;
              // 停止轮询，等待用户手动刷新
              this.stopQRCodePolling();
              break;

            default:
              console.warn('未知的二维码状态:', status);
              break;
          }
        } else {
          console.error('检查二维码状态失败:', res);
        }
      } catch (error) {
        console.error('检查二维码状态异常:', error);
      }
    },

    /**
     * 处理二维码登录
     * 调用登录接口，成功后获取用户信息
     */
    async handleQRCodeLogin() {
      try {
        this.qrStatus = QRCodeStatus.LOGGING;

        if (this.qrCode) {
          const res = await this.$store.dispatch(HANDLE_QR_CODE_LOGIN, {
            token: this.qrCode,
            game_id: this.gameId,
            package_name: this.pkgName
          });
          if (res.result == 1) {
            // 登录成功，获取用户信息
            // await this.getLoginInfo();
            this.$store.commit(SWITCH_SERVER, 0);
            this.$store.commit(UPDATE_PC_SERVER_ID, 0);
            this.$store.commit(UPDATE_ROLE_LIST, []);
            this.$store.dispatch(GET_SERVER_LIST).then(_serverList => {
              this.$store.commit(UPDATE_LOADING, false);
            });
          } else {
            // this.loginResFn(res);
            this.qrStatus = QRCodeStatus.EXPIRED;
          }
        }
      } catch (error) {
        console.error('二维码登录处理异常:', error);
        alert(this.letter.v3ScanLoginError);
        this.qrStatus = QRCodeStatus.EXPIRED;
      }
    },

    /**
     * 开始轮询检查状态
     * 轮询会一直持续，直到：
     * 1. 检测到终止状态（过期、拒绝、授权过期）
     * 2. 用户成功登录
     * 3. 组件卸载或手动清理
     */
    startQRCodePolling() {
      this.stopQRCodePolling();
      // 每3秒检查一次状态
      this.qrCodePollTimer = setInterval(() => {
        this.checkQRCodeStatus();
      }, 3000);
    },

    /**
     * 停止轮询
     */
    stopQRCodePolling() {
      if (this.qrCodePollTimer) {
        clearInterval(this.qrCodePollTimer);
        this.qrCodePollTimer = null;
      }
    },

    /**
     * 刷新二维码
     */
    async refreshQRCode() {
      this.qrCodeLoading = true;
      this.stopQRCodePolling();
      this.qrCodeUrl = '';
      this.qrCode = '';
      this.qrStatus = QRCodeStatus.INIT;
      await this.getQRCode();
    },

    /**
     * 初始化扫码登录
     */
    initQRCodeLogin() {
      this.getQRCode();
    },

    /**
     * 清理资源
     */
    cleanupQRCode() {
      this.stopQRCodePolling();
      this.qrCodeUrl = '';
      this.qrCode = '';
      this.qrStatus = QRCodeStatus.INIT;
    }
  },

  beforeDestroy() {
    // 组件卸载时清理资源
    this.cleanupQRCode();
  }
};
